import { format } from 'date-fns'
import type { EngineActivityEvent } from '../../types'
import { formatPnlPct } from '../../utils/pnl'
import { parseApiDate } from '../../utils/signalExpiry'
import { SCAN_STALE_AFTER_MS } from '../ScanHeartbeat'
import TerminalPanel from './TerminalPanel'
import { ActivityRow, EngineActivityState, groupActivity, standDownLabel } from './engineActivity'
import { displayBase } from '../../utils/displaySymbol'

/** "just now", "12m ago", "3h ago", "2d ago" */
export const timeAgo = (ms: number): string => {
  const minutes = Math.floor(ms / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  return hours < 24 ? `${hours}h ago` : `${Math.floor(hours / 24)}d ago`
}

type Marker = 'scan' | 'idle' | 'win' | 'loss' | 'breakeven'

const MARKERS: Record<Marker, string> = {
  scan: 'bg-sky-400 ring-sky-400/15',
  idle: 'bg-amber-400 ring-amber-400/15',
  win: 'bg-green-400 ring-green-400/20',
  loss: 'bg-red-400 ring-red-400/20',
  breakeven: 'bg-slate-400 ring-slate-400/15',
}

const OUTCOME_PILL: Record<string, string> = {
  win: 'bg-green-500/10 text-green-300 ring-green-500/30',
  loss: 'bg-red-500/10 text-red-300 ring-red-500/30',
  breakeven: 'bg-white/5 text-gray-300 ring-white/10',
}

const markerOf = (event: EngineActivityEvent): Marker =>
  event.kind === 'scan'
    ? event.outcome === 'dispatched'
      ? 'scan'
      : 'idle'
    : event.outcome === 'win' || event.outcome === 'loss'
    ? event.outcome
    : 'breakeven'

const ScanLine = ({ row }: { row: ActivityRow }) => {
  const { event, count } = row
  const at = parseApiDate(event.at)
  return (
    <>
      <span className="font-mono text-gray-500">
        {count > 1 ? `${format(parseApiDate(row.first.at), 'HH:mm')}–${format(at, 'HH:mm')}` : format(at, 'HH:mm')}
      </span>{' '}
      {count > 1 ? 'scans' : 'scan'}
      {event.symbols_scanned != null && (
        <>
          {' · '}
          <span className="font-mono text-gray-200">{event.symbols_scanned}</span> symbols
        </>
      )}
      {event.outcome === 'dispatched' ? (
        event.new_signals != null && (
          <>
            {' · '}
            <span className={`font-mono ${event.new_signals > 0 ? 'font-semibold text-sky-300' : 'text-gray-500'}`}>
              {event.new_signals} new
            </span>
            {count > 1 && (
              <span className="ml-1.5 font-mono text-xs font-semibold text-gray-400" data-testid="engine-event-count">
                ×{count}
              </span>
            )}
          </>
        )
      ) : (
        <>
          {' · '}
          <span className="text-amber-300">
            stood down{event.outcome ? ` — ${standDownLabel(event.outcome)}` : ''}
          </span>
          {count > 1 && (
            <span className="ml-1.5 font-mono text-xs font-semibold text-amber-200" data-testid="engine-event-count">
              ×{count}
            </span>
          )}
        </>
      )}
    </>
  )
}

const CloseLine = ({ event }: { event: EngineActivityEvent }) => {
  const pnl = event.profit_loss_percent
  const outcome = event.outcome ?? 'breakeven'
  return (
    <>
      <span className="font-semibold text-gray-50">{displayBase(event.symbol ?? '')}</span> closed{' '}
      {pnl != null && (
        <span className={`whitespace-nowrap font-mono font-semibold ${pnl > 0 ? 'text-green-400' : pnl < 0 ? 'text-red-400' : 'text-gray-300'}`}>
          {formatPnlPct(pnl)}
        </span>
      )}{' '}
      <span
        className={`rounded px-1.5 py-px align-[1px] text-[10px] font-bold uppercase tracking-wider ring-1 ring-inset ${
          OUTCOME_PILL[outcome] ?? OUTCOME_PILL.breakeven
        }`}
      >
        {outcome}
      </span>
      {event.r_multiple != null && (
        <span className="ml-1.5 whitespace-nowrap font-mono text-xs text-gray-500">
          {event.r_multiple > 0 ? '+' : ''}
          {event.r_multiple.toFixed(2)}R
        </span>
      )}
      {event.excluded_from_record && (
        <span
          className="ml-1.5 rounded bg-amber-500/15 px-1.5 py-px align-[1px] text-[10px] font-bold uppercase tracking-wider text-amber-300 ring-1 ring-inset ring-amber-500/30"
          title={event.exclusion_note ?? 'Excluded from the track record'}
          data-testid="activity-excluded"
        >
          excluded
        </span>
      )}
    </>
  )
}

/**
 * What the engine has been doing: each market scan (symbols covered, signals
 * it opened, or why it stood down) and each signal close, win or loss alike,
 * newest first. Back-to-back scans that stood down for the same reason, or
 * that ran and opened nothing, are one row with a ×N count and their time
 * span; closes and scans that opened signals always keep their own row. The pulse is live while scans are on schedule.
 *
 * The events come from useEngineActivity(), shared with the Dashboard's
 * stand-down banner.
 */
const EngineActivityFeed = ({ activity }: { activity: EngineActivityState }) => {
  const { events, failed, now } = activity

  const lastScan = events?.find((event) => event.kind === 'scan')
  const lastScanAt = lastScan ? parseApiDate(lastScan.at).getTime() : null
  const state = lastScanAt == null ? 'none' : now - lastScanAt > SCAN_STALE_AFTER_MS ? 'stale' : 'live'

  return (
    <TerminalPanel
      title="Engine activity"
      tone="violet"
      className="h-full"
      action={
        events && (
          <span
            className="inline-flex items-center gap-1.5 rounded-full border border-terminal-border bg-terminal-bg/60 px-2 py-0.5 text-[11px] font-medium text-gray-400"
            data-testid="engine-pulse"
            data-state={state}
          >
            <span className="relative flex h-2 w-2" aria-hidden="true">
              {state === 'live' && (
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-60" />
              )}
              <span
                className={`relative inline-flex h-2 w-2 rounded-full ${
                  state === 'live' ? 'bg-green-500' : state === 'stale' ? 'bg-amber-500' : 'bg-gray-500'
                }`}
              />
            </span>
            {state === 'live' ? 'live' : state === 'stale' ? 'scanner quiet' : 'no scan logged'}
          </span>
        )
      }
    >
      {events == null ? (
        <p className="py-10 text-center text-sm text-gray-500">
          {failed ? 'Engine activity is unavailable right now.' : 'Loading activity…'}
        </p>
      ) : events.length === 0 ? (
        <div className="rounded-md border border-dashed border-terminal-border py-10 text-center">
          <p className="text-sm text-gray-300">No engine activity yet</p>
          <p className="mt-1 text-xs text-gray-500">Scans and signal closes appear here as they happen.</p>
        </div>
      ) : (
        <ol className="relative max-h-[26rem] overflow-y-auto pr-1">
          <span
            aria-hidden="true"
            className="absolute bottom-4 left-[7px] top-4 w-px bg-gradient-to-b from-terminal-border via-terminal-border to-transparent"
          />
          {groupActivity(events).map((row) => {
            const { event } = row
            const at = parseApiDate(event.at)
            const marker = markerOf(event)
            return (
              <li
                key={`${event.kind}-${event.signal_id ?? ''}-${event.at}`}
                className="relative flex items-start gap-3 py-2"
                data-testid="engine-event"
                data-kind={event.kind}
                data-outcome={event.outcome ?? undefined}
                data-count={row.count}
              >
                <span
                  aria-hidden="true"
                  className="relative mt-[3px] flex h-[15px] w-[15px] shrink-0 items-center justify-center rounded-full bg-terminal-surface"
                >
                  <span className={`h-2 w-2 rounded-full ring-4 ${MARKERS[marker]}`} />
                </span>
                <p className="min-w-0 flex-1 text-sm leading-snug text-gray-300" data-testid="engine-event-text">
                  {event.kind === 'scan' ? <ScanLine row={row} /> : <CloseLine event={event} />}
                </p>
                <time
                  dateTime={at.toISOString()}
                  title={format(at, 'MMM d, HH:mm')}
                  className="shrink-0 pt-px font-mono text-[11px] tabular-nums text-gray-500"
                >
                  {timeAgo(now - at.getTime())}
                </time>
              </li>
            )
          })}
        </ol>
      )}
    </TerminalPanel>
  )
}

export default EngineActivityFeed
